import { type IPopoverProps } from '@/react/components/core-ui/Popover';
import type { SearchBoxVM } from '@/react/view-models/SearchBoxVM';
import { observer } from 'mobx-react-lite';
import { Suspense, type FC } from 'react';

import { SearchPopover } from '@/react/components/core-ui/SearchPopover';
import { ContentType } from '@/services/isomorphic/ContentService';
import { FetchContentWhenActive } from '@/react/components/content/core/FetchContentWhenActive';
import SkeletonLoader from '@/react/components/utility/SkeletonLoader';
import { SearchSuggestions } from '../../../SearchSuggestions';
import { ProductSuggestions } from '../../../ProductSuggestions';

import S from './styles.base.module.scss';

/**
 * The props for the SearchBoxPopover component.
 *
 * We omit some props from IPopoverProps as they will be overridden
 * by the SearchPopover component itself.
 */
export interface ISearchBoxPopoverProps
  extends Omit<IPopoverProps, 'position' | 'direction' | 'className'> {
  /**
   * The search box view model.
   */
  vm: SearchBoxVM;
  /**
   * The content ID of the popover.
   */
  contentID?: string;
  /**
   * The content type of the popover.
   */
  contentType?: ContentType.Fragment | ContentType.Page | ContentType.Tile;
}

export const SearchBoxPopover: FC<ISearchBoxPopoverProps> = observer(
  function SearchBoxPopover({
    vm,
    contentID = 'custom-search-content',
    contentType = ContentType.Fragment,
    ...props
  }) {
    const { onClose, isOpen } = props;

    return (
      <SearchPopover {...props}>
        <Suspense fallback={<SkeletonLoader className={S.skeletonLoader} />}>
          <div className={S.searchTerms}>
            <SearchSuggestions vm={vm} onNavigate={onClose} />
          </div>
          <div className={S.suggestedProducts}>
            <ProductSuggestions vm={vm} onNavigate={onClose} />
          </div>
          <div className={S.content}>
            <FetchContentWhenActive
              options={{ id: contentID, type: contentType }}
              isActive={isOpen}
            />
          </div>
        </Suspense>
      </SearchPopover>
    );
  }
);
